import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssistantResource from 'Resources/AssistantChat';
import AgentRuns, { type AgentRun } from 'Resources/AgentRuns';
import type { AssistantMessage } from '../../Assistant/types';
import type { DelegationData } from '../components/DelegationChip';
import useAgentRunEvents from './useAgentRunEvents';
import { foldPageActivities, type PageActivity } from './pageActivity';

// The one chat-streaming engine for the AI Workspace (WEB-7957). Owns the live
// stream buffer per conversation and the ways a reply arrives:
//   • sendSSE — this user's own request, streamed inline over SSE
//     (AssistantResource.sendMessageStream)
//   • ingestRunEvent — a headless run (delegation / routine) whose Pusher
//     `agent.runEvent`s the caller routes in after its own gating
//   • sendHeadless — dispatch a run to the worker and watch it to completion:
//     Pusher events fold in live, a DB poll resolves/tails/finalizes the run
//     even if Pusher never delivers a single event (WEB-7802)
// Message-level events (proposals, drafts, charts, …) pass through to the
// caller's handlers; text / thinking / tool status / generated documents
// accumulate in the buffer, which the caller commits to its transcript on done.
//
// Run events are folded seq-ordered along the gap-free frontier (the highest
// seq contiguous from 1): Pusher does not guarantee delivery order, so a later
// text chunk never renders while an earlier one is missing. A detected gap
// triggers a debounced reconcile against the gapless `agent_run_event` rows.

type StreamSource = 'sse' | 'pusher';

type LiveStream = {
	fullText: string;
	hadToolCall: boolean;
	toolStatus: string;
	// One row per tool call this run has made — the Pages build ledger. Empty for
	// every other surface, which simply doesn't read it.
	activities?: PageActivity[];
	activeRunId: string | null;
	controller: AbortController;
	cancelled: boolean;
	thinking: string;
	thinkingDone: boolean;
	suggestedReplies?: string[];
	documents?: NonNullable<AssistantMessage['attachments']>;
	delegations?: DelegationData[];
	// 'sse' = this user's own live request (owns the stream); 'pusher' = a headless
	// run (delegation/routine) we're watching live via agent.runEvent Pusher events.
	source: StreamSource;
	// The numeric agent_run id feeding a 'pusher' buffer. Pins the buffer to one
	// run (a concurrent run's events wait their turn) and keys the DB reconcile.
	runId?: number;
};

type SendSSEOptions = {
	contextType?: string;
	contextId?: number;
	files?: File[];
	isScheduledRun?: boolean;
	runId?: string;
	runTimestamp?: string;
	regenerate?: boolean;
	turnContext?: string;
	trustedContext?: string;
	// The marker this stream's committed message is tagged with (a runTimestamp
	// for a manual routine run, or the followed-up run's id). Read back by the
	// caller via the buffer's `activeRunId`.
	activeRunId?: string | null;
	// Whether this conversation is still the one on screen. Events that mutate
	// the visible transcript are dropped when it isn't — the buffer keeps
	// accumulating so the reply still streams in the background.
	isActive?: () => boolean;
};

type WriteProposalEvent = {
	entity: string;
	action: string;
	data: Record<string, unknown>;
	approvalId?: number | null;
	matchingContacts?: number;
	entityDisplayName?: string;
};

type EmailDraftEvent = {
	contactIds: number[];
	subject: string;
	body: string;
	ccContactIds?: number[];
	clientId?: number;
	attachments?: { id: number; name: string }[];
	approvalId?: number | null;
};

type OfferDraftEvent = {
	clientId: number;
	contactId: number;
	products: {
		productId: number;
		productName?: string;
		quantity: number;
		unitPrice?: number;
		discount?: number;
	}[];
	currency: string;
	validUntil: string;
	description?: string;
	notes?: string;
	approvalId?: number | null;
};

type AgentStreamHandlers = {
	// Fires after the stream buffer is torn down. The final buffer is handed
	// over so the caller can commit its text/thinking/documents as a message.
	onDone?: (final: LiveStream) => void;
	onError?: (error: string) => void;
	onPanel?: (panel: unknown) => void;
	onImages?: (urls: string[]) => void;
	onAttachments?: (attachments: NonNullable<AssistantMessage['attachments']>) => void;
	onMultiAction?: (config: unknown) => void;
	onContactScrape?: (config: unknown) => void;
	onWriteProposal?: (proposal: WriteProposalEvent) => void;
	onEmailDraft?: (draft: EmailDraftEvent) => void;
	onOfferDraft?: (draft: OfferDraftEvent) => void;
	onAgentCreated?: (info: { agentId: number; name: string; updated?: boolean }) => void;
	onIdentityUpdated?: (identity: { name?: string; role?: string; avatarUrl?: string | null }) => void;
};

type RunEventInput = {
	runId: number;
	seq: number;
	eventType: string;
	text: string | null;
	// Carried so the Pages build ledger can fold `tool_use` / `tool_result` into
	// activity rows (WEB-10046). Every other consumer reads `text` only; these are
	// additive and stay undefined for callers that don't supply them.
	payload?: Record<string, unknown> | null;
	at?: number | null;
};

type RunEventRecord = {
	type: string;
	text: string | null;
	payload?: Record<string, unknown> | null;
	/** Event `regDate` as epoch ms, when the source knew it. */
	at?: number | null;
};

type HeadlessWatch = {
	baselineRunId: number;
	runId: number | null;
	finalized: boolean;
	pollTimer: number | null;
	// Fires exactly once, when the watched run reaches a terminal state (from
	// Pusher runFinalized or the DB poll). The buffer is handed over still
	// mounted — the caller reads its text/thinking, then calls end()/stop().
	onFinalized: (status: string, final: LiveStream | null) => void;
	// Liveness, for a watch that adopted a run it did not dispatch (WEB-9953).
	// `status` alone can't answer "is this still alive": a run whose process died
	// stays `running` until the reaper sweeps it two hours later, so a resumed
	// watch would spin forever. Progress does answer it — see `SILENT_RUN_MS`.
	// Left undefined by `sendHeadless`, whose run is alive by construction.
	lastProgressAt?: number;
	lastSeenSeq?: number;
};

/**
 * How old an in-flight run may be before a reload declines to adopt it (WEB-9953).
 *
 * Deliberately far tighter than the 120-minute window the reaper and
 * `presentRunStatus` use (WEB-9606). Those answer "may a reader still call this
 * running?", where finalizing a live run is the expensive mistake. This answers
 * "should a user be shown a spinner and asked to wait?", where the expensive
 * mistake is the opposite one. The longest legitimate run is the worker's
 * 25-minute tool-loop budget (BACKGROUND_TOOL_LOOP_BUDGET_MS), so 30 minutes
 * clears every real run with margin and still refuses obvious corpses.
 */
const RESUMABLE_RUN_MAX_AGE_MS = 30 * 60 * 1000;

/**
 * How long an adopted run may emit nothing at all before we stop believing it.
 *
 * A live run is never quiet for long: text and thinking stream as deltas, and
 * each tool call brackets itself with status/tool_use/tool_result. The longest
 * single blocking step is a saved query, capped server-side at 10s
 * (MAX_EXECUTION_TIME_MS). Ninety seconds of total silence means the process is
 * gone — the status column just hasn't caught up yet.
 */
const SILENT_RUN_MS = 90 * 1000;

// Rebuild a buffer's text/thinking/status from its run's recorded events.
// `seq` is a single monotonic per-run counter across ALL event types, so the
// gap-free frontier is computed over every recorded seq (a tool_use event
// fills a hole just as a text chunk does). Text only advances along the
// frontier; thinking concatenates all its chunks in seq order; tool status is
// the latest seen. Returns whether a gap exists past the frontier.
const foldRunEvents = (live: LiveStream, events: Map<number, RunEventRecord>): boolean => {
	let frontier = 0;
	while (events.has(frontier + 1)) frontier++;
	const seqs = [...events.keys()].sort((a, b) => a - b);
	let fullText = '';
	let hadToolCall = false;
	let thinking = '';
	let toolStatus = '';
	let statusSeq = -1;
	for (const seq of seqs) {
		const ev = events.get(seq) as RunEventRecord;
		// Checked before the null-text guard — a reset carries no text of its own,
		// and replaying the log must discard the abandoned round exactly as the
		// live stream did (WEB-9264).
		if (ev.type === 'text_reset') {
			if (seq <= frontier) {
				fullText = '';
				hadToolCall = false;
			}
			continue;
		}
		if (ev.text === null) continue;
		if (ev.type === 'text') {
			if (seq > frontier) continue;
			if (hadToolCall && fullText && !fullText.endsWith('\n')) fullText += '\n\n';
			hadToolCall = false;
			fullText += ev.text;
		} else if (ev.type === 'status') {
			if (seq <= frontier) hadToolCall = true;
			if (seq > statusSeq) {
				statusSeq = seq;
				toolStatus = ev.text;
			}
		} else if (ev.type === 'thinking') {
			// Thinking streams as incremental deltas — concatenate, same as text.
			thinking += ev.text;
		}
	}
	live.fullText = fullText;
	live.hadToolCall = hadToolCall;
	live.thinking = thinking;
	live.toolStatus = toolStatus;
	// The build ledger (WEB-10046). Folded over ALL seqs, not just the gap-free
	// frontier: a tool_use/tool_result pair is self-describing via callId, so an
	// undelivered text chunk ahead of it must not hide a call that demonstrably
	// happened. Text is the only thing that has to stay ordered.
	live.activities = foldPageActivities(events);
	const maxSeq = seqs.length ? seqs[seqs.length - 1] : 0;
	// Only a hole BETWEEN events counts as a gap. A dropped TAIL (the last
	// `tool_result` of a call) leaves maxSeq on the frontier and is invisible
	// here — deliberately, because "a call has no result yet" is also what an
	// in-flight call looks like, and reporting that as a gap would fire a
	// reconcile several times a second for the whole run. `pollWatchedRun`
	// already reconciles from the DB on every tick while a run is live, which
	// covers the dropped tail without the extra traffic, and
	// `foldPageActivities` settles any row still running once the run's `done`
	// event lands (WEB-11511).
	return maxSeq > frontier;
};

const useAgentStream = () => {
	const streamsRef = useRef<Map<number, LiveStream>>(new Map());
	// Every run event seen (or fetched) for a run, keyed runId → seq. Kept
	// outside the buffer so seqs of non-stream events (run_plan, tool_use, …)
	// still advance the frontier, and so a run's early events survive until a
	// buffer exists to fold them into.
	const runEventsRef = useRef<Map<number, Map<number, RunEventRecord>>>(new Map());
	const watchesRef = useRef<Map<number, HeadlessWatch>>(new Map());
	// Debounced immediate reconcile per run: fired the moment a gap is detected
	// so the missing chunks are pulled from the (gapless) DB right away, instead
	// of waiting for the periodic poll (WEB-7802).
	const reconcileTimersRef = useRef<Map<number, number>>(new Map());
	// Assigned below to dodge the declaration-order cycle
	// (ingest → scheduleReconcile → reconcileFromDb → ingest).
	const reconcileFromDbRef = useRef<(convId: number, runId: number) => void>(() => {});
	const [, setStreamTick] = useState(0);
	const bump = useCallback(() => setStreamTick(t => t + 1), []);

	const get = useCallback(
		(convId: number | null): LiveStream | undefined =>
			convId !== null ? streamsRef.current.get(convId) : undefined,
		[]
	);

	const clearRunBookkeeping = useCallback((runId: number | undefined) => {
		if (runId == null) return;
		runEventsRef.current.delete(runId);
		const timer = reconcileTimersRef.current.get(runId);
		if (timer != null) {
			window.clearTimeout(timer);
			reconcileTimersRef.current.delete(runId);
		}
	}, []);

	const end = useCallback(
		(convId: number) => {
			clearRunBookkeeping(streamsRef.current.get(convId)?.runId);
			if (streamsRef.current.delete(convId)) bump();
		},
		[bump, clearRunBookkeeping]
	);

	// Stop watching a headless run without touching its buffer — the run keeps
	// executing on the worker, we just no longer follow it (no auto-apply, no
	// finalize callback).
	const stopWatching = useCallback((convId: number) => {
		const watch = watchesRef.current.get(convId);
		if (!watch) return;
		if (watch.pollTimer != null) window.clearInterval(watch.pollTimer);
		watchesRef.current.delete(convId);
	}, []);

	// Abort + remove the conversation's stream (and any headless watch on it).
	// Returns the final buffer so the caller can commit whatever streamed
	// before the stop (null = no stream).
	const stop = useCallback(
		(convId: number): LiveStream | null => {
			stopWatching(convId);
			const live = streamsRef.current.get(convId);
			if (!live) return null;
			live.cancelled = true;
			try {
				live.controller.abort();
			} catch {
				/* safe */
			}
			clearRunBookkeeping(live.runId);
			streamsRef.current.delete(convId);
			bump();
			return live;
		},
		[bump, clearRunBookkeeping, stopWatching]
	);

	const clearAll = useCallback(() => {
		for (const convId of [...watchesRef.current.keys()]) stopWatching(convId);
		for (const [, live] of streamsRef.current) {
			try {
				live.controller.abort();
			} catch {
				/* safe */
			}
		}
		streamsRef.current.clear();
		for (const timer of reconcileTimersRef.current.values()) window.clearTimeout(timer);
		reconcileTimersRef.current.clear();
		runEventsRef.current.clear();
		bump();
	}, [bump, stopWatching]);

	const scheduleReconcile = useCallback((convId: number, runId: number) => {
		if (reconcileTimersRef.current.has(runId)) return; // one in flight already
		reconcileTimersRef.current.set(
			runId,
			window.setTimeout(() => {
				reconcileTimersRef.current.delete(runId);
				reconcileFromDbRef.current(convId, runId);
			}, 250)
		);
	}, []);

	// Fold a batch of run events (live Pusher OR the DB catch-up) into the
	// conversation's buffer. Records every seq (any type), creates the 'pusher'
	// buffer on the first stream-content event, and repaints once per batch.
	const ingestRunEvents = useCallback(
		(convId: number, runId: number, batch: ReadonlyArray<Omit<RunEventInput, 'runId'>>) => {
			const existing = streamsRef.current.get(convId);
			if (existing && existing.source === 'sse') return; // local request owns this conversation
			let events = runEventsRef.current.get(runId);
			if (!events) {
				events = new Map();
				runEventsRef.current.set(runId, events);
			}
			for (const ev of batch) {
				// Keep the FIRST `at` we saw for a seq. Pusher arrival time (ms, one
				// clock) and the DB's `regDate` (second-granular, another clock) are
				// not comparable, and the ledger subtracts one event's `at` from
				// another's — letting a reconcile overwrite half a pair with a
				// different clock produced nonsense durations (WEB-10046).
				const prior = events.get(ev.seq);
				events.set(ev.seq, {
					type: ev.eventType,
					text: ev.text,
					payload: ev.payload,
					at: prior?.at ?? ev.at
				});
			}
			let live = existing;
			if (!live) {
				// Only stream content creates a buffer — a run that never streams
				// (e.g. a Playbook run) must not flip the conversation into a
				// "streaming" state just because its plan/tool events were recorded.
				const anyContent = [...events.values()].some(
					e => e.text !== null && (e.type === 'text' || e.type === 'status' || e.type === 'thinking')
				);
				if (!anyContent) return;
				live = {
					fullText: '',
					hadToolCall: false,
					toolStatus: '',
					// Must be null: the streaming bubble in a non-run-based conversation
					// (a delegated child) is gated on `!activeRunId`. activeRunId only
					// matters for run-grouped rendering, where it's matched against a
					// run's *timestamp* marker — not the numeric runId.
					activeRunId: null,
					controller: new AbortController(),
					cancelled: false,
					thinking: '',
					thinkingDone: false,
					source: 'pusher',
					runId
				};
				streamsRef.current.set(convId, live);
			} else if (live.runId !== runId) {
				// Buffer pinned to another run — this run's events stay recorded and
				// surface once the pinned run finalizes (or via the reload).
				return;
			}
			const hasGap = foldRunEvents(live, events);
			bump();
			if (hasGap) {
				// TEMP instrumentation (WEB-7802): we're holding text past the frontier
				// because an earlier seq hasn't arrived. Remove once the reconcile is proven.
				let frontier = 0;
				while (events.has(frontier + 1)) frontier++;
				const seqs = [...events.keys()];
				const maxSeq = seqs.length ? Math.max(...seqs) : 0;
				let missing = 0;
				for (let s = frontier + 1; s <= maxSeq; s++) if (!events.has(s)) missing++;
				console.warn(
					`[pages-stream] GAP: frontier=${frontier} maxSeq=${maxSeq} missingCount=${missing} ` +
						`shownChars=${live.fullText.length}`
				);
				// Pull the missing chunks from the gapless DB immediately rather than
				// waiting for the periodic poll (WEB-7802).
				scheduleReconcile(convId, runId);
			}
		},
		[bump, scheduleReconcile]
	);

	// Feed a single Pusher `agent.runEvent` into the conversation's buffer. The
	// caller does its own routing/gating (which conversation, which runs to
	// drop); this records the seq and folds stream content.
	const ingestRunEvent = useCallback(
		(convId: number, ev: RunEventInput) => {
			ingestRunEvents(convId, ev.runId, [ev]);
		},
		[ingestRunEvents]
	);

	// Self-heal dropped Pusher events (WEB-7802). Pusher is best-effort — a lost
	// text chunk stalls the frontier until finalize. The persisted
	// `agent_run_event` rows are gapless, so fetch everything past the current
	// frontier and fold it in; a dropped middle chunk fills, a dropped tail
	// chunk appears. Best-effort: the finalize reload is the ultimate backstop.
	const reconcileFromDb = useCallback(
		async (convId: number, runId: number) => {
			const isRelevant = () =>
				watchesRef.current.get(convId)?.runId === runId || streamsRef.current.get(convId)?.runId === runId;
			if (!isRelevant()) return;
			const events = runEventsRef.current.get(runId);
			let frontier = 0;
			while (events?.has(frontier + 1)) frontier++;
			try {
				const fetched = await AgentRuns.listEvents(runId, { offset: frontier, limit: 500 });
				// TEMP instrumentation (WEB-7802): warn (not debug) so it shows without
				// Verbose — did the DB catch-up find the chunks Pusher hadn't delivered?
				console.warn(
					`[pages-stream] reconcile runId=${runId} frontier=${frontier} fetched=${fetched.length}` +
						(fetched.length ? ` firstSeq=${fetched[0].seq} lastSeq=${fetched[fetched.length - 1].seq}` : '')
				);
				if (!isRelevant() || !fetched.length) return;
				ingestRunEvents(
					convId,
					runId,
					fetched.map(e => ({
						seq: e.seq,
						eventType: e.eventType,
						text: typeof e.payload?.text === 'string' ? (e.payload.text as string) : null,
						payload: e.payload,
						// No `at`: `regDate` is second-granular, so it can't time a tool
						// call, and mixing it with Pusher arrival times is worse than
						// showing no duration. An event only ever seen here simply
						// contributes no timing (WEB-10046).
						at: null
					}))
				);
			} catch (err) {
				console.warn('[useAgentStream] stream reconcile failed', err);
			}
		},
		[ingestRunEvents]
	);
	reconcileFromDbRef.current = (convId, runId) => void reconcileFromDb(convId, runId);

	// The watched run reached a terminal state — stop following it and hand the
	// final buffer to the caller. Idempotent: a duplicate finalize (Pusher +
	// DB poll racing) is a no-op.
	const finalizeWatch = useCallback(
		(convId: number, status: string) => {
			const watch = watchesRef.current.get(convId);
			if (!watch || watch.finalized) return;
			watch.finalized = true;
			stopWatching(convId);
			watch.onFinalized(status, streamsRef.current.get(convId) ?? null);
		},
		[stopWatching]
	);

	// PRIMARY delivery path for headless runs — fully Pusher-independent
	// (WEB-7802). Pusher delivery can drop chunks, and a long-lived tab's socket
	// can go silent entirely across a worker restart. This poll (a) resolves the
	// current run for the conversation straight from the DB, (b) tails its
	// gapless event log, and (c) finalizes from the DB run status — so streaming
	// and completion work even if Pusher never delivers a single event. Live
	// Pusher events still fold in when healthy (seqs dedupe); short requests, no
	// long-connection timeout risk.
	const pollWatchedRun = useCallback(
		async (convId: number) => {
			const watch = watchesRef.current.get(convId);
			if (!watch || watch.finalized) return;
			let status: string | null = null;
			try {
				const runs = await AgentRuns.list({ conversationId: convId, limit: 1 });
				const run = runs?.data?.[0];
				// Only adopt a run newer than the pre-dispatch baseline, so we never
				// latch onto (and finalize) a prior terminal run for this conversation.
				if (run && run.id > watch.baselineRunId) {
					status = run.status;
					if (watch.runId == null) watch.runId = run.id;
				}
			} catch (err) {
				console.warn('[pages-stream] run resolve failed', err);
			}
			// The worker hasn't created our run yet — wait for the next tick.
			if (watch.runId == null) return;
			await reconcileFromDb(convId, watch.runId);
			// Finalize from DB status when Pusher's runFinalized never arrives. Only
			// terminal states; running / awaiting_* keep streaming. finalizeWatch is
			// idempotent, so a duplicate Pusher finalize is safe.
			if (status === 'succeeded' || status === 'failed' || status === 'cancelled') {
				finalizeWatch(convId, status);
				return;
			}
			// Liveness, for an adopted run only (WEB-9953). `sendHeadless` leaves
			// `lastProgressAt` undefined because it just dispatched the run itself.
			if (watch.lastProgressAt == null) return;
			const seqs = runEventsRef.current.get(watch.runId);
			const seen = seqs ? seqs.size : 0;
			if (seen !== watch.lastSeenSeq) {
				watch.lastSeenSeq = seen;
				watch.lastProgressAt = Date.now();
				return;
			}
			if (Date.now() - watch.lastProgressAt > SILENT_RUN_MS) {
				// The row still says `running`, but nothing has come out of it for
				// long enough that the process is gone. Report it the way the reaper
				// eventually will, rather than leaving the user on a spinner until it
				// sweeps (WEB-9606, two hours later).
				console.warn(`[pages-stream] adopted run ${watch.runId} went silent — treating as timed out`);
				finalizeWatch(convId, 'timed_out');
			}
		},
		[reconcileFromDb, finalizeWatch]
	);

	/**
	 * Adopt a run that is ALREADY in flight on this conversation and follow it to
	 * completion — the reload / navigate-away-and-back case (WEB-9953).
	 *
	 * Everything this needs already existed: the backend mirrors every stream
	 * event into `agent_run_event`, `listEvents` replays them gaplessly, and
	 * Pusher `agent.runEvent` carries `conversationId` precisely so a late
	 * subscriber can match it (WEB-6800). The only missing piece was that nobody
	 * asked, on mount, whether a run was still live. So: resolve it, replay what
	 * already streamed, then let the existing poll + Pusher path tail it.
	 *
	 * Returns the adopted run id, or null when there is nothing live to resume.
	 */
	const watchExistingRun = useCallback(
		async (convId: number, handlers: { onFinalized: HeadlessWatch['onFinalized'] }): Promise<number | null> => {
			// A watch already following this conversation owns it — never stack two.
			if (watchesRef.current.has(convId)) return null;
			let run: AgentRun | undefined;
			try {
				const runs = await AgentRuns.list({ conversationId: convId, status: 'running', limit: 1 });
				run = runs?.data?.[0];
			} catch (err) {
				// Best-effort: failing to resume is the pre-WEB-9953 behaviour, which
				// is degraded but not broken. Never block opening the conversation.
				console.warn('[pages-stream] resume lookup failed', err);
				return null;
			}
			if (!run) return null;
			const startedAt = run.startedAt ? new Date(run.startedAt).getTime() : NaN;
			if (isNaN(startedAt) || Date.now() - startedAt > RESUMABLE_RUN_MAX_AGE_MS) return null;

			const watch: HeadlessWatch = {
				// `pollWatchedRun` adopts the newest run strictly above the baseline,
				// so sit one below this run's id to make it adopt exactly this one.
				baselineRunId: run.id - 1,
				runId: run.id,
				finalized: false,
				pollTimer: null,
				onFinalized: handlers.onFinalized,
				lastProgressAt: Date.now(),
				lastSeenSeq: 0
			};
			watchesRef.current.set(convId, watch);
			// Replay from seq 0 — the buffer is empty after a reload, so everything
			// the user already saw (and everything they missed) comes back at once.
			await reconcileFromDb(convId, run.id);
			// The caller may have navigated away again while that round-tripped.
			if (watchesRef.current.get(convId) !== watch || watch.finalized) return run.id;
			watch.pollTimer = window.setInterval(() => void pollWatchedRun(convId), 400);
			return run.id;
		},
		[reconcileFromDb, pollWatchedRun]
	);

	// Dispatch a headless run on the conversation and watch it to completion.
	// The worker runs the agent and streams back over Pusher; completion is
	// reported via `onFinalized` (exactly once). Throws when the dispatch
	// itself fails — no watch is left behind.
	const sendHeadless = useCallback(
		async (
			convId: number,
			message: string,
			opts: { files?: File[]; regenerate?: boolean },
			handlers: { onFinalized: HeadlessWatch['onFinalized'] }
		) => {
			// Reset any previous stream/watch for this conversation.
			stopWatching(convId);
			end(convId);
			const watch: HeadlessWatch = {
				baselineRunId: 0,
				runId: null,
				finalized: false,
				pollTimer: null,
				onFinalized: handlers.onFinalized
			};
			watchesRef.current.set(convId, watch);
			// Record the newest existing run so the DB fallback poll can distinguish
			// our (about-to-be-created) run from a prior terminal one (WEB-7802).
			try {
				const prev = await AgentRuns.list({ conversationId: convId, limit: 1 });
				watch.baselineRunId = prev?.data?.[0]?.id ?? 0;
			} catch (err) {
				console.warn('[pages-stream] baseline run lookup failed', err);
				watch.baselineRunId = 0;
			}
			try {
				await AssistantResource.sendMessageHeadless(convId, message, opts.files, opts.regenerate);
			} catch (err) {
				stopWatching(convId);
				throw err;
			}
			// The run may already have finalized (or the watch been stopped) while
			// the dispatch round-tripped — don't start a poll nothing will clear.
			if (watchesRef.current.get(convId) !== watch || watch.finalized) return;
			watch.pollTimer = window.setInterval(() => void pollWatchedRun(convId), 400);
		},
		[end, stopWatching, pollWatchedRun]
	);

	// Route Pusher run events into active headless watches. Callers that do
	// their own routing (WorkspaceChat's delegation/routine view, with its
	// playbook and own-run gating) subscribe separately and call ingestRunEvent
	// themselves — watches and manual ingestion never overlap on a conversation.
	const matchWatch = useCallback((cid: number | null | undefined, runId: number): number | null => {
		if (cid != null && watchesRef.current.has(cid)) return cid;
		for (const [convId, watch] of watchesRef.current) {
			if (watch.runId === runId) return convId;
		}
		return null;
	}, []);

	useAgentRunEvents({
		onRunStarted: data => {
			if (data.conversationId == null) return;
			const watch = watchesRef.current.get(data.conversationId);
			if (watch && watch.runId == null) watch.runId = data.runId;
		},
		onRunEvent: data => {
			const convId = matchWatch(data.conversationId, data.runId);
			if (convId == null) return;
			const watch = watchesRef.current.get(convId) as HeadlessWatch;
			// Ensure the reconcile has a runId even if we missed onRunStarted (a
			// watcher that subscribed late still gets runId on every event) — WEB-7802.
			watch.runId = data.runId;
			const payload = (data.payload ?? {}) as { text?: unknown };
			// TEMP instrumentation (WEB-7802): what seq/type arrives, in what order.
			console.debug(`[pages-stream] recv seq=${data.seq} type=${data.eventType}`);
			ingestRunEvent(convId, {
				runId: data.runId,
				seq: data.seq,
				eventType: data.eventType,
				text: typeof payload.text === 'string' ? payload.text : null,
				payload: data.payload ?? null,
				// Pusher carries no regDate. Arrival time is close enough for the
				// only thing it's used for — a per-call duration on the ledger — and
				// the DB reconcile overwrites it with the real one if it ever runs.
				at: Date.now()
			});
		},
		onRunFinalized: data => {
			const convId = matchWatch(data.conversationId, data.runId);
			if (convId == null) return;
			finalizeWatch(convId, data.status);
		}
	});

	// Unmount: stop every poll/reconcile timer. Buffers die with the component.
	useEffect(
		() => () => {
			for (const watch of watchesRef.current.values()) {
				if (watch.pollTimer != null) window.clearInterval(watch.pollTimer);
			}
			for (const timer of reconcileTimersRef.current.values()) window.clearTimeout(timer);
		},
		[]
	);

	const sendSSE = useCallback(
		async (convId: number, message: string, opts: SendSSEOptions, handlers: AgentStreamHandlers) => {
			const isActive = opts.isActive ?? (() => true);
			const live: LiveStream = {
				fullText: '',
				hadToolCall: false,
				toolStatus: '',
				activeRunId: opts.activeRunId ?? null,
				controller: new AbortController(),
				cancelled: false,
				thinking: '',
				thinkingDone: false,
				source: 'sse'
			};
			streamsRef.current.set(convId, live);
			bump();

			try {
				await AssistantResource.sendMessageStream(
					convId,
					message,
					opts.contextType ?? 'general',
					opts.contextId,
					{
						files: opts.files,
						isScheduledRun: opts.isScheduledRun,
						runId: opts.runId,
						runTimestamp: opts.runTimestamp,
						regenerate: opts.regenerate,
						turnContext: opts.turnContext,
						trustedContext: opts.trustedContext,
						signal: live.controller.signal,
						onText: chunk => {
							if (live.cancelled) return;
							if (live.thinking && !live.thinkingDone) {
								live.thinkingDone = true;
							}
							if (live.hadToolCall && live.fullText.length > 0 && !live.fullText.endsWith('\n')) {
								live.fullText += '\n\n';
							}
							live.hadToolCall = false;
							live.fullText += chunk;
							bump();
						},
						// WEB-9264: the loop abandoned the round it just streamed and is
						// replacing it. Drop what we have shown so the user reads only the
						// surviving answer instead of the discarded one glued to it.
						onTextReset: () => {
							if (live.cancelled) return;
							live.fullText = '';
							live.hadToolCall = false;
							bump();
						},
						onStatus: status => {
							if (live.cancelled) return;
							live.hadToolCall = true;
							live.toolStatus = status;
							bump();
						},
						onThinking: delta => {
							if (live.cancelled) return;
							// Thinking streams as incremental deltas — concatenate.
							live.thinking += delta;
							bump();
						},
						onSuggestedReplies: replies => {
							if (live.cancelled || !isActive()) return;
							live.suggestedReplies = replies;
							bump();
						},
						onDocument: attachment => {
							if (live.cancelled || !isActive()) return;
							// Collect generated files for the caller to stamp onto the
							// committed assistant message in onDone — same shape the reload
							// path hydrates from metadata.attachments, so live and reload
							// render identically. No bump: documents only render once onDone
							// builds the message, so there's nothing to repaint mid-stream.
							live.documents = [...(live.documents ?? []), attachment];
						},
						onDelegation: delegation => {
							if (live.cancelled || !isActive()) return;
							live.delegations = [...(live.delegations ?? []), delegation];
							bump();
						},
						onDone: () => {
							if (live.cancelled) return;
							end(convId);
							handlers.onDone?.(live);
						},
						onError: error => {
							if (live.cancelled) return;
							end(convId);
							handlers.onError?.(error);
						},
						onPanel: panel => {
							if (live.cancelled || !isActive()) return;
							handlers.onPanel?.(panel);
						},
						onImages: urls => {
							if (live.cancelled || !isActive()) return;
							handlers.onImages?.(urls);
						},
						onAttachments: attachments => {
							if (live.cancelled || !isActive()) return;
							handlers.onAttachments?.(attachments);
						},
						onMultiAction: config => {
							if (live.cancelled || !isActive()) return;
							handlers.onMultiAction?.(config);
						},
						onContactScrape: config => {
							if (live.cancelled || !isActive()) return;
							handlers.onContactScrape?.(config);
						},
						onWriteProposal: proposal => {
							if (live.cancelled || !isActive()) return;
							handlers.onWriteProposal?.(proposal);
						},
						onEmailDraft: draft => {
							if (live.cancelled || !isActive()) return;
							handlers.onEmailDraft?.(draft);
						},
						onOfferDraft: draft => {
							if (live.cancelled || !isActive()) return;
							handlers.onOfferDraft?.(draft);
						},
						onAgentCreated: info => {
							if (live.cancelled || !isActive()) return;
							handlers.onAgentCreated?.(info);
						},
						onIdentityUpdated: identity => {
							if (live.cancelled) return;
							handlers.onIdentityUpdated?.(identity);
						}
					}
				);
			} finally {
				end(convId);
			}
		},
		[bump, end]
	);

	return useMemo(
		() => ({ get, end, stop, clearAll, sendSSE, ingestRunEvent, sendHeadless, watchExistingRun, stopWatching }),
		[get, end, stop, clearAll, sendSSE, ingestRunEvent, sendHeadless, watchExistingRun, stopWatching]
	);
};

export default useAgentStream;
export type {
	LiveStream,
	AgentStreamHandlers,
	SendSSEOptions,
	RunEventInput,
	WriteProposalEvent,
	EmailDraftEvent,
	OfferDraftEvent
};
